import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import Board from './Board.jsx';
import EvalBar from './EvalBar.jsx';
import HelpPanel from './HelpPanel.jsx';
import useSpeech from './useSpeech.js';
import useStockfish from './useStockfish.js';
import { parseCommand, resolveMove, PIECE_NAMES } from './commandParser.mjs';
import { findOpening, START_FEN } from './openings.mjs';
import { positionWarnings } from './boardState.mjs';

const EMPTY_FEN = '8/8/8/8/8/8/8/8 w - - 0 1';
const MAX_LOG = 60;

function loadGame(fen) {
  // skipValidation lets us work with setup positions (e.g. no kings yet).
  return new Chess(fen, { skipValidation: true });
}

let logId = 0;

// Human-readable result of a just-played move on `clone`.
function describeResult(clone, move) {
  let msg = move.san;
  let suffix = '';
  if (clone.isCheckmate()) {
    msg += ' — checkmate!';
    suffix = ', checkmate';
  } else if (clone.isStalemate()) {
    msg += ' — stalemate.';
    suffix = ', stalemate';
  } else if (clone.isCheck()) {
    msg += ' — check.';
    suffix = ', check';
  }
  return { msg, suffix };
}

export default function VoiceChess() {
  // Position history as a stack of { fen, lastMove } with a pointer,
  // so undo/redo covers moves *and* setup edits uniformly.
  const [history, setHistory] = useState({
    stack: [{ fen: START_FEN, lastMove: null }],
    ptr: 0,
  });
  const [flipped, setFlipped] = useState(false);
  const [evalOn, setEvalOn] = useState(false);
  const [speakOn, setSpeakOn] = useState(true);
  const [muted, setMuted] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [log, setLog] = useState([]);
  const [typedCommand, setTypedCommand] = useState('');
  const [fenInput, setFenInput] = useState('');
  const mutedRef = useRef(false);

  const current = history.stack[history.ptr];
  const game = useMemo(() => loadGame(current.fen), [current.fen]);
  const warnings = useMemo(() => positionWarnings(current.fen), [current.fen]);
  const { evaluation, status: engineStatus } = useStockfish(
    evalOn,
    current.fen,
    warnings.length === 0
  );

  const addLog = useCallback((text, result, ok) => {
    setLog((prev) => [{ id: ++logId, text, result, ok }, ...prev].slice(0, MAX_LOG));
  }, []);

  const pushPosition = useCallback((fen, lastMove) => {
    setHistory((prev) => {
      const stack = prev.stack.slice(0, prev.ptr + 1);
      stack.push({ fen, lastMove: lastMove || null });
      return { stack, ptr: stack.length - 1 };
    });
  }, []);

  // --- Voice feedback. The mic is paused while speaking so the app
  // doesn't hear its own confirmations as commands. ---
  const speechRef = useRef(null);
  const speak = useCallback((text) => {
    if (!window.speechSynthesis) return;
    setSpeakOn((on) => {
      if (on) {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        utter.rate = 1.1;
        const s = speechRef.current;
        if (s) s.pause();
        utter.onend = () => setTimeout(() => s && s.resume(), 200);
        utter.onerror = () => s && s.resume();
        window.speechSynthesis.speak(utter);
      }
      return on;
    });
  }, []);

  const spokenMove = (move) => {
    const piece = PIECE_NAMES[move.piece];
    if (move.san === 'O-O' || move.san.startsWith('O-O+')) return 'castles kingside';
    if (move.san.startsWith('O-O-O')) return 'castles queenside';
    const verb = move.flags.includes('c') || move.flags.includes('e') ? 'takes' : 'to';
    let text = `${piece} ${verb} ${move.to}`;
    if (move.promotion) text += `, promoting to ${PIECE_NAMES[move.promotion]}`;
    return text;
  };

  const applyCommand = useCallback(
    (text, source) => {
      const intent = parseCommand(text);

      // Voice mute: ignore everything except "start listening".
      if (mutedRef.current && source === 'voice') {
        if (intent && intent.type === 'listen' && intent.on) {
          mutedRef.current = false;
          setMuted(false);
          addLog(text, 'Listening again.', true);
          speak('Listening.');
        }
        return;
      }

      const fail = (msg) => {
        addLog(text, msg, false);
        speak(msg);
      };

      const finishMove = (clone, move, cmdText) => {
        const { msg, suffix } = describeResult(clone, move);
        pushPosition(clone.fen(), { from: move.from, to: move.to });
        addLog(cmdText, msg, true);
        speak(spokenMove(move) + suffix);
      };

      if (!intent) {
        // Typed input gets one more chance as standard algebraic notation.
        if (source === 'typed') {
          try {
            const clone = loadGame(current.fen);
            const move = clone.move(text.trim());
            finishMove(clone, move, text);
            return;
          } catch {
            /* fall through */
          }
        }
        fail("Didn't catch a command — say \"help\" for the list.");
        return;
      }

      switch (intent.type) {
        case 'help':
          setHelpOpen(true);
          addLog(text, 'Showing command list — say "close" when done.', true);
          return;

        case 'close':
          setHelpOpen(false);
          addLog(text, 'Closed.', true);
          return;

        case 'listen':
          if (intent.on === false) {
            mutedRef.current = true;
            setMuted(true);
            addLog(text, 'Muted — say "start listening" to resume.', true);
            speak('Muted.');
          } else {
            mutedRef.current = false;
            setMuted(false);
            addLog(text, 'Listening.', true);
          }
          return;

        case 'eval': {
          const next = intent.on === null ? !evalOn : intent.on;
          setEvalOn(next);
          addLog(text, `Evaluation bar ${next ? 'on' : 'off'}.`, true);
          speak(`Evaluation ${next ? 'on' : 'off'}.`);
          return;
        }

        case 'flip':
          setFlipped((f) => !f);
          addLog(text, 'Board flipped.', true);
          return;

        case 'reset':
          pushPosition(START_FEN, null);
          addLog(text, 'Board reset to the starting position.', true);
          speak('New game.');
          return;

        case 'undo': {
          if (history.ptr === 0) {
            fail('Nothing to undo.');
            return;
          }
          const restored = loadGame(history.stack[history.ptr - 1].fen);
          const desc = `Undone — ${restored.turn() === 'w' ? 'White' : 'Black'} to move.`;
          setHistory({ ...history, ptr: history.ptr - 1 });
          addLog(text, desc, true);
          speak(desc);
          return;
        }

        case 'redo': {
          if (history.ptr >= history.stack.length - 1) {
            fail('Nothing to redo.');
            return;
          }
          const restored = loadGame(history.stack[history.ptr + 1].fen);
          const desc = `Redone — ${restored.turn() === 'w' ? 'White' : 'Black'} to move.`;
          setHistory({ ...history, ptr: history.ptr + 1 });
          addLog(text, desc, true);
          speak(desc);
          return;
        }

        case 'clearBoard':
          pushPosition(EMPTY_FEN, null);
          addLog(text, 'Board cleared — place pieces, then say whose move it is.', true);
          speak('Board cleared.');
          return;

        case 'turn': {
          if (game.turn() === intent.color) {
            addLog(text, `Already ${intent.color === 'w' ? 'White' : 'Black'} to move.`, true);
            return;
          }
          const parts = current.fen.split(' ');
          parts[1] = intent.color;
          parts[3] = '-'; // stale en-passant square would be illegal
          pushPosition(parts.join(' '), current.lastMove);
          addLog(text, `${intent.color === 'w' ? 'White' : 'Black'} to move.`, true);
          return;
        }

        case 'place': {
          const clone = loadGame(current.fen);
          const ok = clone.put({ type: intent.piece, color: intent.color }, intent.square);
          if (!ok) {
            fail(`Can't place a ${PIECE_NAMES[intent.piece]} on ${intent.square}.`);
            return;
          }
          pushPosition(clone.fen(), { from: intent.square, to: intent.square });
          const desc = `${intent.color === 'w' ? 'White' : 'Black'} ${PIECE_NAMES[intent.piece]} on ${intent.square}.`;
          addLog(text, desc, true);
          speak(desc);
          return;
        }

        case 'remove': {
          const clone = loadGame(current.fen);
          const removed = clone.remove(intent.square);
          if (!removed) {
            fail(`No piece on ${intent.square}.`);
            return;
          }
          pushPosition(clone.fen(), null);
          const desc = `Removed the ${PIECE_NAMES[removed.type]} from ${intent.square}.`;
          addLog(text, desc, true);
          speak(desc);
          return;
        }

        case 'load': {
          const opening = findOpening(intent.words);
          if (!opening) {
            fail("Didn't recognize that position name — say \"help\" for the list.");
            return;
          }
          pushPosition(opening.fen, null);
          addLog(text, `Loaded: ${opening.name}.`, true);
          speak(`Loaded the ${opening.name}.`);
          return;
        }

        case 'forceMove': {
          if (!intent.from || !intent.to) {
            fail('Say sudo with both squares, like "sudo d1 to h3".');
            return;
          }
          const clone = loadGame(current.fen);
          const piece = clone.get(intent.from);
          if (!piece) {
            fail(`No piece on ${intent.from}.`);
            return;
          }
          clone.remove(intent.from);
          clone.remove(intent.to); // captures whatever was there
          if (!clone.put(piece, intent.to)) {
            fail(`Couldn't force the ${PIECE_NAMES[piece.type]} onto ${intent.to}.`);
            return;
          }
          pushPosition(clone.fen(), { from: intent.from, to: intent.to });
          const desc = `Forced ${PIECE_NAMES[piece.type]} ${intent.from} → ${intent.to}.`;
          addLog(text, desc, true);
          speak(desc);
          return;
        }

        case 'castle':
        case 'move': {
          let legal;
          try {
            legal = game.moves({ verbose: true });
          } catch {
            fail('No legal moves in this position.');
            return;
          }
          const resolved = resolveMove(intent, legal);
          if (resolved.error) {
            let msg = resolved.error;
            if (resolved.options) msg += ` (${resolved.options.join(', ')})`;
            fail(msg);
            return;
          }
          const clone = loadGame(current.fen);
          const move = clone.move({
            from: resolved.move.from,
            to: resolved.move.to,
            promotion: resolved.move.promotion,
          });
          finishMove(clone, move, text);
          return;
        }

        default:
          fail("Didn't catch a command — say \"help\" for the list.");
      }
    },
    [addLog, current, evalOn, game, history, pushPosition, speak]
  );

  const speech = useSpeech((text) => applyCommand(text, 'voice'));
  speechRef.current = speech;

  // Drag-and-drop / click-click moves from the board. If the dragged piece
  // belongs to the side not on move, the turn flips automatically — this is
  // a position-study board, both sides are yours.
  const handleBoardMove = useCallback(
    (from, to) => {
      const findMove = (g) => {
        try {
          return g
            .moves({ verbose: true })
            .filter((m) => m.from === from && m.to === to)
            .sort((a, b) => (a.promotion === 'q' ? -1 : 1) - (b.promotion === 'q' ? -1 : 1))[0];
        } catch {
          return undefined;
        }
      };

      let clone = loadGame(current.fen);
      let candidate = findMove(clone);
      if (!candidate) {
        const piece = clone.get(from);
        if (piece && piece.color !== clone.turn()) {
          const parts = current.fen.split(' ');
          parts[1] = piece.color;
          parts[3] = '-';
          try {
            clone = loadGame(parts.join(' '));
            candidate = findMove(clone);
          } catch {
            /* leave candidate undefined */
          }
        }
      }
      if (!candidate) {
        addLog(`${from} → ${to}`, 'Not a legal move.', false);
        return;
      }
      const move = clone.move({
        from: candidate.from,
        to: candidate.to,
        promotion: candidate.promotion,
      });
      const { msg } = describeResult(clone, move);
      pushPosition(clone.fen(), { from: move.from, to: move.to });
      addLog(`${from} → ${to}`, msg, true);
    },
    [addLog, current, pushPosition]
  );

  const handleTyped = (e) => {
    e.preventDefault();
    const text = typedCommand.trim();
    if (!text) return;
    applyCommand(text, 'typed');
    setTypedCommand('');
  };

  const handleFenSet = (e) => {
    e.preventDefault();
    const fen = fenInput.trim();
    if (!fen) return;
    try {
      const clone = loadGame(fen);
      pushPosition(clone.fen(), null);
      addLog('set FEN', 'Position loaded from FEN.', true);
      setFenInput('');
    } catch (err) {
      addLog('set FEN', `Invalid FEN: ${err.message}`, false);
    }
  };

  // Status line + check highlight.
  const bothKings = /k/.test(current.fen.split(' ')[0]) && /K/.test(current.fen.split(' ')[0]);
  let statusText = `${game.turn() === 'w' ? 'White' : 'Black'} to move`;
  let checkSquare = null;
  if (bothKings) {
    if (game.isCheckmate()) {
      statusText = `Checkmate — ${game.turn() === 'w' ? 'Black' : 'White'} wins`;
    } else if (game.isStalemate()) {
      statusText = 'Stalemate';
    } else if (game.isCheck()) {
      statusText += ' — check';
    }
    if (game.isCheck() || game.isCheckmate()) {
      outer: for (const rank of game.board()) {
        for (const cell of rank) {
          if (cell && cell.type === 'k' && cell.color === game.turn()) {
            checkSquare = cell.square;
            break outer;
          }
        }
      }
    }
  } else {
    statusText += ' (setup position)';
  }

  return (
    <div className="vc-app">
      <header className="vc-header">
        <h1>Voice Chess</h1>
        <p>A hands-free board — say "help" (or press Help) for everything it understands.</p>
      </header>

      <div className="vc-main">
        {evalOn && <EvalBar evaluation={evaluation} status={engineStatus} />}
        <Board
          board={game.board()}
          flipped={flipped}
          lastMove={current.lastMove}
          checkSquare={checkSquare}
          onMove={handleBoardMove}
        />
        <div className="vc-side">
          <div className="vc-mic-row">
            {speech.supported ? (
              <>
                <button
                  className={`vc-btn vc-mic-btn ${speech.listening ? 'active' : ''}`}
                  onClick={speech.listening ? speech.stop : speech.start}
                >
                  {speech.listening ? '■ Stop mic' : '🎤 Start listening'}
                </button>
                <span className={`vc-mic-status ${speech.listening ? (muted ? 'muted' : 'live') : ''}`}>
                  {speech.listening
                    ? muted
                      ? 'Muted — say "start listening"'
                      : speech.interim || 'Listening…'
                    : speech.error || 'Mic off'}
                </span>
              </>
            ) : (
              <span className="vc-mic-status">
                This browser has no speech recognition (try Chrome or Edge) — the text box below
                still works.
              </span>
            )}
          </div>
          <div className="vc-status">{statusText}</div>
          {warnings.length > 0 && (
            <div className="vc-warning">
              ⚠ Invalid position: {warnings.join('; ')}. Engine eval is paused.
            </div>
          )}
          <div className="vc-controls">
            <button className="vc-btn" onClick={() => applyCommand('undo', 'typed')}>Undo</button>
            <button className="vc-btn" onClick={() => applyCommand('redo', 'typed')}>Redo</button>
            <button className="vc-btn" onClick={() => applyCommand('reset', 'typed')}>Reset</button>
            <button className="vc-btn" onClick={() => setFlipped((f) => !f)}>Flip</button>
            <button
              className={`vc-btn ${evalOn ? 'toggled' : ''}`}
              onClick={() => setEvalOn((v) => !v)}
            >
              Eval {evalOn ? 'on' : 'off'}
            </button>
            <button
              className={`vc-btn ${speakOn ? 'toggled' : ''}`}
              onClick={() => setSpeakOn((v) => !v)}
            >
              Speak {speakOn ? 'on' : 'off'}
            </button>
            <button className="vc-btn" onClick={() => setHelpOpen((v) => !v)}>Help</button>
          </div>

          <form className="vc-typed" onSubmit={handleTyped}>
            <input
              type="text"
              value={typedCommand}
              onChange={(e) => setTypedCommand(e.target.value)}
              placeholder='Type a command ("knight to f3", "Nf3", "reset")'
            />
            <button className="vc-btn" type="submit">Go</button>
          </form>

          <form className="vc-typed vc-fen" onSubmit={handleFenSet}>
            <input
              type="text"
              value={fenInput}
              onChange={(e) => setFenInput(e.target.value)}
              placeholder="Paste a FEN to set the position"
            />
            <button className="vc-btn" type="submit">Set</button>
            <button
              className="vc-btn"
              type="button"
              onClick={() => navigator.clipboard && navigator.clipboard.writeText(current.fen)}
              title={current.fen}
            >
              Copy FEN
            </button>
          </form>

          <div className="vc-log">
            {log.length === 0 && (
              <div className="vc-log-empty">
                Commands you say (or type) show up here with what happened.
              </div>
            )}
            {log.map((entry) => (
              <div key={entry.id} className={`vc-log-entry ${entry.ok ? 'ok' : 'err'}`}>
                <span className="vc-log-cmd">“{entry.text}”</span>
                <span className="vc-log-result">{entry.result}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
